import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { isValid, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@uidotdev/usehooks";
import { cn } from "@/utils/cn";
import { Tabs } from "@/components/ui/tabs";
import type { TabConfig } from "@/components/ui/tabs";
import { PageContainer } from "@/components/ui/page-container";
import { BasicDataTab } from "@/components/staff-member-profile/basic-data";
import { CalendarTab } from "@/components/staff-member-profile/calendar";
import { StaffEventDetail } from "@/components/staff-member-profile/calendar/staff-event-detail";
import {
  useStaffMember,
  staffMemberQueryOptions,
} from "@/queries/use-staff-members";
import { UserAvatar } from "@/components/ui/user-avatar";
import { RouterContext } from "@/types/router";
import { buildRoute } from "@/utils/navigation";
import { breakpoints } from "@/styles/theme";

const VALID_TABS = ["basic-data", "salary", "stats", "calendar"] as const;
type ValidTab = (typeof VALID_TABS)[number];

export const Route = createFileRoute("/(protected)/staff/$staffMemberId/")({
  loader: async ({
    params,
    context,
  }: {
    params: { staffMemberId: string };
    context: RouterContext;
  }) => {
    const staffMember = await context.queryClient.ensureQueryData(
      staffMemberQueryOptions(Number(params.staffMemberId)),
    );
    return {
      breadcrumb: `${staffMember.firstName} ${staffMember.lastName}`,
    };
  },
  component: RouteComponent,
  validateSearch: (search) => {
    const tab = search.tab as string;
    return {
      tab: VALID_TABS.includes(tab as ValidTab) ? tab : "basic-data",
      eventId: search.eventId != null ? Number(search.eventId) : undefined,
      week: typeof search.week === "string" ? search.week : undefined,
    };
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { tab, eventId, week } = Route.useSearch();
  const { staffMemberId } = Route.useParams();
  const staffMemberIdNum = Number(staffMemberId);
  const { t } = useTranslation();
  const isMobile = !useMediaQuery(`(min-width: ${breakpoints.md})`);
  const {
    data: staffMember,
    isLoading,
    error,
  } = useStaffMember(staffMemberIdNum);

  const handleTabChange = (newTab: string) => {
    navigate({
      search: { tab: newTab, eventId: undefined, week },
    });
  };

  const handleBack = () => {
    navigate({
      to: buildRoute.staffProfile(staffMemberIdNum),
      search: { tab: "calendar", eventId: undefined, week },
    });
  };

  const initialDate = useMemo(() => {
    if (!week) return undefined;
    const parsed = parseISO(week);
    return isValid(parsed) ? parsed : undefined;
  }, [week]);

  const isCalendarTab = tab === "calendar";
  const showDetailOnMobile = isMobile && isCalendarTab && eventId != null;

  useEffect(() => {
    if (!isMobile && eventId != null) {
      navigate({
        to: buildRoute.staffProfile(staffMemberIdNum),
        search: { tab: "calendar", eventId: undefined, week },
      });
    }
  }, [isMobile, eventId, navigate, staffMemberIdNum, week]);

  const tabs: TabConfig[] = [
    {
      value: "basic-data",
      label: t("pages:profile.tabs.basicData"),
      content: staffMember ? (
        <BasicDataTab
          staffMember={staffMember}
          isLoading={isLoading}
          error={error}
        />
      ) : null,
    },
    {
      value: "salary",
      label: t("pages:profile.tabs.salary"),
      content: <></>,
      disabled: true,
    },
    {
      value: "stats",
      label: t("pages:profile.tabs.statistics"),
      content: <></>,
      disabled: true,
    },
    {
      value: "calendar",
      label: t("pages:profile.tabs.calendar"),
      content: staffMember ? (
        <CalendarTab staffMember={staffMember} initialDate={initialDate} />
      ) : null,
    },
    {
      value: "role",
      label: t("pages:profile.tabs.role"),
      content: <></>,
      disabled: true,
    },
  ];

  return (
    <>
      <div
        className={cn(
          isCalendarTab
            ? "flex h-full flex-col"
            : "flex min-h-0 flex-1 flex-col",
          showDetailOnMobile && "hidden",
        )}
      >
        <div className="mt-6 flex items-center gap-3 px-6 md:hidden">
          {staffMember && (
            <>
              <UserAvatar
                variant="table"
                firstName={staffMember.firstName}
                lastName={staffMember.lastName}
                avatarUrl={staffMember.avatarUrl}
              />
              <span className="text-lg font-bold text-gray-800 dark:text-white">
                {staffMember.firstName} {staffMember.lastName}
              </span>
            </>
          )}
        </div>
        <PageContainer
          className={
            isCalendarTab
              ? "mx-6 mb-0 h-full flex-1 overflow-hidden rounded-b-none px-2 pt-2 pb-0 md:mb-0 lg:mb-0"
              : "mx-6"
          }
        >
          <Tabs tabs={tabs} value={tab} onValueChange={handleTabChange} lazy />
        </PageContainer>
      </div>

      {showDetailOnMobile && staffMember && (
        <StaffEventDetail
          staffMember={staffMember}
          eventId={eventId}
          onBack={handleBack}
        />
      )}
    </>
  );
}
